"use client";

import React, { useEffect, useRef, useState } from "react";
import { LineChart, Line, AreaChart, Area, RadialBarChart, RadialBar, BarChart, Bar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { Activity, Zap, Shield, Waves } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const attenuationData = [{
  distance: "0km",
  value: 0.25
}, {
  distance: "10km",
  value: 0.22
}, {
  distance: "20km",
  value: 0.20
}, {
  distance: "30km",
  value: 0.19
}, {
  distance: "40km",
  value: 0.18
}];
const bandwidthData = [{
  year: "2021",
  capacity: 100
}, {
  year: "2022",
  capacity: 200
}, {
  year: "2023",
  capacity: 400
}, {
  year: "2024",
  capacity: 800
}];
const durabilityData = [{
  condition: "Standard",
  lifespan: 25
}, {
  condition: "Harsh",
  lifespan: 22
}, {
  condition: "Extreme",
  lifespan: 20
}];
const bendData = [{
  name: "Standard",
  value: 30,
  fill: "var(--color-muted)"
}, {
  name: "G657A2",
  value: 100,
  fill: "var(--color-primary)"
}];
const chartConfigs = {
  attenuation: {
    value: {
      label: "Attenuation (dB/km)",
      color: "var(--chart-1)"
    }
  },
  bandwidth: {
    capacity: {
      label: "Capacity (Gbps)",
      color: "var(--chart-2)"
    }
  },
  durability: {
    lifespan: {
      label: "Lifespan (Years)",
      color: "var(--chart-3)"
    }
  },
  bend: {
    value: {
      label: "Resistance Score",
      color: "var(--chart-4)"
    }
  }
};
const advantages = [{
  id: "low-attenuation",
  title: "Low Attenuation",
  description: "Industry-leading signal integrity over extreme distances for global telecom networks.",
  icon: Waves,
  metric: "< 0.18",
  unit: "dB/km",
  chart: <ChartContainer config={chartConfigs.attenuation} className="h-[120px] w-full mt-4">
        <LineChart data={attenuationData} margin={{
      top: 5,
      right: 5,
      left: 5,
      bottom: 5
    }}>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Line type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={3} dot={{
        r: 4,
        fill: "var(--color-value)"
      }} activeDot={{
        r: 6
      }} isAnimationActive={true} />
        </LineChart>
      </ChartContainer>
}, {
  id: "bending-resistance",
  title: "Bending Resistance",
  description: "G657A2 compliant fibers engineered to withstand tight routing in dense AI data centers.",
  icon: Activity,
  metric: "7.5",
  unit: "mm Radius",
  chart: <ChartContainer config={chartConfigs.bend} className="h-[120px] w-full mt-4">
        <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%" barSize={12} data={bendData} startAngle={180} endAngle={0}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background={{
        fill: "var(--color-muted)",
        opacity: 0.2
      }} dataKey="value" cornerRadius={10} isAnimationActive={true} />
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
        </RadialBarChart>
      </ChartContainer>
}, {
  id: "high-bandwidth",
  title: "High Bandwidth",
  description: "Future-proof capacity supporting 400G+ transmissions for next-gen infrastructure.",
  icon: Zap,
  metric: "400G+",
  unit: "Ready",
  chart: <ChartContainer config={chartConfigs.bandwidth} className="h-[120px] w-full mt-4">
        <AreaChart data={bandwidthData} margin={{
      top: 5,
      right: 5,
      left: 5,
      bottom: 5
    }}>
          <defs>
            <linearGradient id="colorCapacity" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-capacity)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--color-capacity)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Area type="monotone" dataKey="capacity" stroke="var(--color-capacity)" fillOpacity={1} fill="url(#colorCapacity)" strokeWidth={2} isAnimationActive={true} />
        </AreaChart>
      </ChartContainer>
}, {
  id: "durability",
  title: "Extreme Durability",
  description: "Robust construction ensuring long-term reliability in harsh environmental conditions.",
  icon: Shield,
  metric: "25+",
  unit: "Years",
  chart: <ChartContainer config={chartConfigs.durability} className="h-[120px] w-full mt-4">
        <BarChart data={durabilityData} margin={{
      top: 5,
      right: 5,
      left: 5,
      bottom: 5
    }}>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Bar dataKey="lifespan" fill="var(--color-lifespan)" radius={[4, 4, 0, 0]} barSize={32} isAnimationActive={true} />
        </BarChart>
      </ChartContainer>
}];
function AnimatedCard({
  item,
  index
}: {
  item: typeof advantages[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="relative h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-colors duration-500 group">
        {}
        <div className={cn("absolute inset-0 pointer-events-none transition-opacity duration-500 bg-[linear-gradient(to_right,var(--color-foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-foreground)_1px,transparent_1px)] bg-[size:24px_24px]", index === 1 ? "opacity-10" : "opacity-[0.03]")} />
        
        {}
        <div className="absolute -inset-px bg-gradient-to-b from-primary/20 to-transparent opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl" />

        <CardHeader className="relative z-10 pb-2">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
              <item.icon className="size-5" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-mono font-bold text-foreground tracking-tight">
                {item.metric}
              </div>
              <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                {item.unit}
              </div>
            </div>
          </div>
          <CardTitle className="text-xl">{item.title}</CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {item.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="relative z-10 pt-0">
          {}
          {isVisible && item.chart}
        </CardContent>
      </Card>
    </div>;
}
export default function EngineeringAdvantages() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="engineering-advantages" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-primary/30 blur-[120px] rounded-full mix-blend-screen" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mb-16 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-mono font-medium mb-6 border border-primary/20">
            <span className="relative flex size-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full size-2 bg-primary"></span>
            </span>
            Core Specifications
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Engineering <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-2">Advantages</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Precision-engineered optical fibers and drone components designed for maximum performance, reliability, and longevity in critical infrastructure and high-stress environments.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {advantages.map((item, i) => <AnimatedCard key={item.id} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}